<template>
  <div>
    <dx-grid
      :url="url"
      name="employer-childcare-benefit-requests"
      :columns="columns"
      :title="title"
      :actions="actions"
      :beforeSetupGrid="beforeSetupGrid"
      :onRowClicked="onRowClicked"
      navigationPath="onRowClicked"
      navigationField="onRowClicked"
      ref="grid"
    ></dx-grid>

    <!-- ── Add request dialog ── -->
    <v-dialog v-model="addDialog" max-width="1120" persistent scrollable>
      <v-card class="ecb-dialog">
        <div class="ecb-header">
          <div class="ecb-header-left">
            <div class="ecb-header-name">
              <v-icon color="white" class="mr-2">mdi-plus-circle</v-icon>
              Add Employer Childcare Benefit Request
            </div>
            <div class="ecb-header-ref" v-if="addTerm">
              Term: {{ addTerm }}
            </div>
          </div>
        </div>

        <form class="ecb-body ecb-add-body" autocomplete="off" @submit.prevent>
          <v-alert
            v-if="addExistingRequest"
            :value="true"
            type="warning"
            outline
            class="mb-3 mt-0"
          >
            This account already has a request for {{ addTerm || "this term"
            }}<span v-if="addExistingRequest.employerName">
              ({{ addExistingRequest.employerName }})</span
            >.
            <v-btn
              small
              color="warning"
              class="ml-2 mt-1"
              @click="openExistingFromAdd"
            >
              Open Existing Request
            </v-btn>
          </v-alert>

          <div class="ecb-add-grid">
            <!-- ── Left column: who ── -->
            <div class="ecb-add-col">
              <div class="ecb-panel">
                <div class="ecb-panel-head">
                  <v-icon small color="#2e7d32" class="mr-1"
                    >mdi-account-group</v-icon
                  >
                  Family
                </div>
                <assign-account-lookup
                  ref="addAccountLookup"
                  @selected="onAddAccountSelected"
                ></assign-account-lookup>
              </div>

              <div
                class="ecb-panel"
                v-if="addAccountId && !loadingAccountInfo && !addExistingRequest"
              >
                <div class="ecb-panel-head">
                  <v-icon small color="#2e7d32" class="mr-1">mdi-domain</v-icon>
                  Employer
                </div>
                <business-lookup
                  ref="addBusinessLookup"
                  label="Select business"
                  @selected="onAddBusinessSelected"
                ></business-lookup>

                <div class="ecb-panel-divider">Employer details</div>

                <v-layout row wrap class="ecb-employer-fields">
                <v-flex xs12 sm6>
                  <v-text-field
                    v-model="addForm.employerName"
                    label="Business name *"
                    maxlength="250"
                    autocomplete="new-password"
                    name="ecb-employer-name"
                    :error="addEmployerNameInvalid"
                    :error-messages="
                      addEmployerNameInvalid
                        ? 'Business name is required.'
                        : ''
                    "
                    :hide-details="!addEmployerNameInvalid"
                  ></v-text-field>
                </v-flex>
                <v-flex xs12 sm6>
                  <v-text-field
                    v-model="addForm.contactPerson"
                    label="Contact person *"
                    maxlength="250"
                    autocomplete="new-password"
                    name="ecb-contact-person"
                    :error="addContactPersonInvalid"
                    :error-messages="
                      addContactPersonInvalid
                        ? 'Contact person is required.'
                        : ''
                    "
                    :hide-details="!addContactPersonInvalid"
                  ></v-text-field>
                </v-flex>
                <v-flex xs12 sm6>
                  <v-combobox
                    v-model="addForm.contactPersonTitle"
                    :items="contactPersonTitleOptions"
                    label="Title"
                    hide-no-data
                    hide-details
                    maxlength="100"
                    autocomplete="new-password"
                    name="ecb-contact-title"
                  ></v-combobox>
                </v-flex>
                <v-flex xs12 sm6>
                  <v-text-field
                    v-model="addForm.employerPhone"
                    label="Phone *"
                    maxlength="30"
                    autocomplete="new-password"
                    name="ecb-employer-phone"
                    :error="addEmployerPhoneInvalid"
                    :error-messages="
                      addEmployerPhoneInvalid
                        ? 'A valid phone number is required.'
                        : ''
                    "
                    :hide-details="!addEmployerPhoneInvalid"
                  ></v-text-field>
                </v-flex>
                <v-flex xs12 sm6>
                  <v-text-field
                    v-model="addForm.employerEmail"
                    label="Email *"
                    maxlength="250"
                    autocomplete="new-password"
                    name="ecb-employer-email"
                    :error="addEmployerEmailInvalid"
                    :error-messages="
                      addEmployerEmailInvalid
                        ? 'A valid email address is required.'
                        : ''
                    "
                    :hide-details="!addEmployerEmailInvalid"
                  ></v-text-field>
                </v-flex>
                <v-flex xs12 sm6>
                  <v-text-field
                    v-model="addForm.note"
                    label="Note"
                    hide-details
                    autocomplete="off"
                    name="ecb-employer-note"
                  ></v-text-field>
                </v-flex>
              </v-layout>
              </div>
            </div>

            <!-- ── Right column: which children ── -->
            <div class="ecb-add-col">
              <div class="ecb-panel ecb-panel--fill">
              <div class="ecb-panel-head">
                <v-icon small color="#2e7d32" class="mr-1"
                  >mdi-account-child</v-icon
                >
                Child Care Students
                <v-spacer></v-spacer>
                <span
                  v-if="
                    addAccountId && !loadingAccountInfo && !addExistingRequest
                  "
                  class="ecb-members-count"
                  >{{ selectableAddStudents.length }}</span
                >
              </div>

              <div v-if="loadingAccountInfo" class="ecb-add-loading">
                <v-progress-circular
                  indeterminate
                  color="success"
                  size="20"
                  width="2"
                  class="mr-2"
                ></v-progress-circular>
                Loading students…
              </div>

              <div
                v-else-if="!addAccountId"
                class="ecb-add-placeholder"
              >
                <v-icon size="34" color="#cfd8dc">mdi-arrow-left</v-icon>
                <div class="mt-2">Pick a family to see their children.</div>
              </div>

              <div
                v-else-if="addExistingRequest"
                class="ecb-add-placeholder"
              >
                <v-icon size="34" color="#ffcc80"
                  >mdi-alert-circle-outline</v-icon
                >
                <div class="mt-2">
                  Open the existing request to change its students.
                </div>
              </div>

              <template v-else>
              <div class="ecb-add-hint mb-2">
                Select the child(ren) with Child Care charges. Amounts are
                pre-discount, not the final price.
              </div>

              <v-alert
                v-if="addStudents.length && !selectableAddStudents.length"
                :value="true"
                type="info"
                outline
                class="mb-2"
              >
                None of this family's children currently qualify for the
                Employer-Paid Child Care Program. The reason for each child is
                shown below.
              </v-alert>

              <div
                v-if="addStudents.length"
                class="ecb-add-student-list"
                :class="{ 'ecb-add-student-list--error': addStudentsInvalid }"
              >
                <v-checkbox
                  v-if="selectableAddStudents.length > 1"
                  color="success"
                  hide-details
                  class="ecb-add-student-item mt-0"
                  :input-value="allAddStudentsSelected"
                  :indeterminate="someAddStudentsSelected"
                  @change="toggleAllAddStudents"
                >
                  <template slot="label">
                    <span class="ecb-add-student-name">Select all</span>
                  </template>
                </v-checkbox>
                <v-checkbox
                  v-for="student in addStudents"
                  :key="normalizeStudentId(student.studentID)"
                  v-model="addSelectedStudentIds"
                  :value="normalizeStudentId(student.studentID)"
                  color="success"
                  hide-details
                  class="ecb-add-student-item mt-0"
                  :class="{
                    'ecb-add-student-item--disabled': !isEmployerBenefitEligible(
                      student
                    ),
                  }"
                  :disabled="!isEmployerBenefitEligible(student)"
                >
                  <template slot="label">
                    <span class="ecb-add-student-name">{{
                      student.engName || student.hebName || student.studentID
                    }}</span>
                    <span v-if="student.class" class="ecb-add-student-meta">
                      Class {{ student.class }}
                    </span>
                    <span
                      v-if="isEmployerBenefitEligible(student)"
                      class="ecb-add-student-price"
                    >
                      {{ formatCurrency(getChildCare(student)) }}
                      <span class="ecb-add-price-note">pre-discount</span>
                    </span>
                    <span
                      v-else
                      class="ecb-add-student-ineligible"
                    >
                      {{ ineligibleReason(student) }}
                    </span>
                  </template>
                </v-checkbox>
              </div>
              <div v-else class="ecb-td-empty">
                No Child Care students were found on this account for this
                school year.
              </div>
              <div
                v-if="addStudentsInvalid"
                class="ecb-add-error-text"
              >
                Please select at least one child care student.
              </div>
              <div
                v-if="selectableAddStudents.length"
                class="ecb-add-total"
              >
                <span>Pre-discount total</span>
                <span>{{ formatCurrency(addSelectedTotal) }}</span>
              </div>
              </template>
              </div>
            </div>
          </div>
        </form>

        <div class="ecb-footer">
          <v-btn color="default" dark @click="closeAddDialog">Cancel</v-btn>
          <v-spacer></v-spacer>
          <v-btn
            color="success"
            :loading="creating"
            :disabled="!canSubmitAdd"
            @click="createRequest"
          >
            <v-icon left small>mdi-content-save</v-icon>
            Submit Request
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- ── Details dialog ── -->
    <v-dialog v-model="detailsDialog" max-width="920" persistent scrollable>
      <v-card class="ecb-dialog">
        <!-- Header -->
        <div class="ecb-header">
          <div class="ecb-header-left">
            <div class="ecb-header-name">
              <span class="ecb-header-id">#{{ clickedRequest.iD }}</span>
              {{ clickedRequest.nameH || "Request" }}
            </div>
            <div class="ecb-header-ref">
              Account: {{ clickedRequest.accountID || "-" }} &nbsp;|&nbsp; Term:
              {{ clickedRequest.term || "-" }} &nbsp;|&nbsp; Saved:
              {{ formatDateTime(clickedRequest.dateSaved) }}
              <span v-if="requestSource">
                &nbsp;|&nbsp; Source:
                <v-chip
                  small
                  label
                  class="ecb-source-chip"
                  :color="requestSource === 'ONLINE' ? '#e3f2fd' : '#fff3e0'"
                  :text-color="requestSource === 'ONLINE' ? '#1565c0' : '#ef6c00'"
                >
                  <v-icon left small>{{
                    requestSource === "ONLINE" ? "mdi-web" : "mdi-account-tie"
                  }}</v-icon>
                  {{ requestSource === "ONLINE" ? "Online" : "Admin Portal" }}
                </v-chip>
              </span>
              <span v-if="requestedAgentName" title="Agent the parent indicated when submitting — for reference only, does not set the business's billing agent.">
                &nbsp;|&nbsp; Requested Agent:
                <v-chip small label class="ecb-source-chip" color="#ede7f6" text-color="#5e35b1">
                  <v-icon left small>mdi-briefcase-account-outline</v-icon>
                  {{ requestedAgentName }}
                </v-chip>
              </span>
            </div>
          </div>
          <div class="ecb-header-right">
            <v-chip
              class="ecb-status-chip"
              color="white"
              :text-color="getStatusColor(clickedRequest.status)"
              small
            >
              <v-icon left small :color="getStatusColor(clickedRequest.status)">
                {{ getStatusIcon(clickedRequest.status) }}
              </v-icon>
              {{ clickedRequest.status || "—" }}
            </v-chip>
            <div class="ecb-sent-meta" v-if="clickedRequest.sentToEmployer">
              <v-icon
                small
                style="color:rgba(255,255,255,0.8);vertical-align:middle"
                >mdi-email-check-outline</v-icon
              >
              Sent {{ formatDateTime(clickedRequest.sentDate) }}
              <span v-if="clickedRequest.sentBy">
                by {{ clickedRequest.sentBy }}
              </span>
            </div>
          </div>
        </div>

        <div class="ecb-body">
          <!-- Business link panel — the primary, most prominent action -->
          <div
            class="ecb-link-section"
            :class="{
              'ecb-link-section--linked': linkedBusiness,
              'ecb-link-section--loading': loadingBusiness,
            }"
          >
            <div class="ecb-link-section-header">
              <v-icon
                :color="
                  loadingBusiness
                    ? '#90a4ae'
                    : linkedBusiness
                    ? '#2e7d32'
                    : '#1565c0'
                "
                class="mr-2"
                >{{
                  loadingBusiness
                    ? "mdi-domain"
                    : linkedBusiness
                    ? "mdi-domain"
                    : "mdi-link-variant"
                }}</v-icon
              >
              <div>
                <div class="ecb-link-section-title">
                  {{
                    loadingBusiness
                      ? "Business"
                      : linkedBusiness
                      ? "Linked to Business"
                      : "Link to a Business"
                  }}
                </div>
                <div
                  class="ecb-link-section-sub"
                  v-if="!loadingBusiness && !linkedBusiness"
                >
                  Search for the employer below, or create a new business, then
                  choose which children go on their bill.
                </div>
              </div>
            </div>

            <div v-if="loadingBusiness" class="ecb-business-loading">
              <v-progress-circular
                indeterminate
                color="success"
                size="22"
                width="2"
                class="mr-2"
              ></v-progress-circular>
              Loading business…
            </div>

            <!-- Linked state -->
            <div v-else-if="linkedBusiness" class="ecb-business-linked-card">
              <v-icon color="#2e7d32" size="30" class="mr-3">mdi-domain</v-icon>
              <div class="ecb-business-linked-info">
                <div class="ecb-business-linked-name">
                  {{ linkedBusiness.name }}
                </div>
                <div
                  class="ecb-business-linked-meta"
                  v-if="linkedBusiness.contactPerson"
                >
                  {{ linkedBusiness.contactPerson }}
                </div>
              </div>
              <v-btn
                color="success"
                small
                :to="{
                  name: 'BusinessView',
                  params: { id: linkedBusiness.businessID },
                }"
              >
                Open Business
                <v-icon right small>mdi-arrow-right</v-icon>
              </v-btn>
              <v-btn
                small
                flat
                class="ecb-unlink-btn"
                :loading="unlinkingBusiness"
                @click="openUnlinkBusinessDialog"
              >
                <v-icon left small>mdi-link-off</v-icon>
                Unlink
              </v-btn>
            </div>

            <!-- Unlinked state — only after we know there is no linked business -->
            <div v-else class="ecb-business-picker">
              <business-lookup
                ref="detailsBusinessLookup"
                @selected="onBusinessToLinkSelected"
              ></business-lookup>
              <div v-if="linkCoverageStudents.length" class="ecb-link-students mt-3">
                <div class="caption grey--text mb-2">
                  Choose which children to put on this employer's bill. Uncheck
                  a child to leave them off.
                </div>
                <v-layout row wrap class="mb-2">
                  <v-flex xs12 sm6 class="pr-2">
                    <v-menu
                      v-model="linkStartMenu"
                      :close-on-content-click="false"
                      offset-y
                    >
                      <template v-slot:activator="{ on }">
                        <v-text-field
                          :value="formatDate(linkCoverageStart)"
                          label="Coverage start"
                          outline
                          dense
                          readonly
                          hide-details
                          prepend-inner-icon="mdi-calendar"
                          v-on="on"
                        ></v-text-field>
                      </template>
                      <v-date-picker
                        v-model="linkCoverageStart"
                        @input="linkStartMenu = false"
                      ></v-date-picker>
                    </v-menu>
                  </v-flex>
                  <v-flex xs12 sm6 class="pl-2">
                    <v-menu
                      v-model="linkEndMenu"
                      :close-on-content-click="false"
                      offset-y
                    >
                      <template v-slot:activator="{ on }">
                        <v-text-field
                          :value="formatDate(linkCoverageEnd)"
                          label="Coverage end"
                          outline
                          dense
                          readonly
                          hide-details
                          prepend-inner-icon="mdi-calendar"
                          v-on="on"
                        ></v-text-field>
                      </template>
                      <v-date-picker
                        v-model="linkCoverageEnd"
                        @input="linkEndMenu = false"
                      ></v-date-picker>
                    </v-menu>
                  </v-flex>
                </v-layout>
                <v-checkbox
                  v-for="student in linkCoverageStudents"
                  :key="normalizeStudentId(student.studentID)"
                  v-model="linkSelectedStudentIds"
                  :value="normalizeStudentId(student.studentID)"
                  color="success"
                  hide-details
                  class="mt-0"
                >
                  <template slot="label">
                    <span>{{
                      student.engName || student.hebName || student.studentID
                    }}</span>
                    <span v-if="student.class" class="caption grey--text ml-2">
                      {{ student.class }}
                    </span>
                  </template>
                </v-checkbox>
              </div>
              <v-btn
                small
                color="success"
                class="mt-2"
                :disabled="!businessToLink || !linkSelectedStudentIds.length"
                :loading="linkingBusiness"
                @click="linkBusiness"
              >
                <v-icon left small>mdi-link</v-icon>
                Link selected children
              </v-btn>
            </div>

            <!-- Children on this bill — shown once linked, right under the link -->
            <div v-if="linkedBusiness" class="ecb-members-header mt-3">
              <span class="ecb-members-title">Children on this bill</span>
              <span class="ecb-members-count">{{ students.length }}</span>
              <v-progress-circular
                v-if="loadingStudents"
                indeterminate
                color="success"
                size="18"
                width="2"
                class="ml-2"
              ></v-progress-circular>
            </div>
            <div v-if="linkedBusiness" class="ecb-members-wrap">
              <table class="ecb-members-table">
                <thead>
                  <tr>
                    <th style="width:120px">Student ID</th>
                    <th>English Name</th>
                    <th>Hebrew Name</th>
                    <th style="width:90px">Class</th>
                    <th style="width:90px">On bill</th>
                    <th style="width:180px">Coverage</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="student in students"
                    :key="student.id"
                    class="ecb-member-row"
                  >
                    <td>{{ (student.studentID || "").trim() }}</td>
                    <td class="ecb-td-name">{{ student.engName }}</td>
                    <td>{{ student.hebName }}</td>
                    <td>{{ student.class }}</td>
                    <td>{{ student.linked ? "Yes" : "No" }}</td>
                    <td>
                      <span v-if="student.linked">
                        {{ formatDate(student.coverageStartDate) }} –
                        {{ formatDate(student.coverageEndDate) }}
                      </span>
                      <span v-else>—</span>
                    </td>
                  </tr>
                  <tr v-if="!loadingStudents && !students.length">
                    <td colspan="6" class="ecb-td-empty">
                      No students found for this request.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Compact update panel — secondary, small footprint -->
          <div class="ecb-update-panel-compact">
            <v-layout row wrap align-center>
              <v-flex xs12 sm3 class="pr-2">
                <v-combobox
                  v-model="updateForm.status"
                  :items="statusOptions"
                  label="Status"
                  prepend-inner-icon="mdi-flag-variant"
                  dense
                  outline
                  hide-details
                ></v-combobox>
              </v-flex>
              <v-flex xs12 sm7 class="px-2">
                <v-text-field
                  v-model="updateForm.note"
                  label="Note"
                  prepend-inner-icon="mdi-note-text"
                  dense
                  outline
                  hide-details
                ></v-text-field>
              </v-flex>
              <v-flex xs12 sm2 class="pl-2 text-xs-right">
                <v-btn small color="success" :loading="saving" @click="saveRequest">
                  <v-icon left small>mdi-content-save</v-icon>
                  Save
                </v-btn>
              </v-flex>
            </v-layout>
          </div>

          <!-- Family / Employer info — secondary, compact reference -->
          <v-layout row wrap class="ecb-sections ecb-sections--secondary">
            <!-- Left column: family / contact -->
            <v-flex xs12 sm6 class="ecb-col pr-3">
              <div class="ecb-section ecb-section--secondary">
                <div class="ecb-section-title ecb-section-title--secondary">
                  Family Info
                </div>
                <div class="ecb-row">
                  <span class="ecb-label">Account ID</span>
                  <span class="ecb-val">{{
                    clickedRequest.accountID || "-"
                  }}</span>
                </div>
                <div class="ecb-row">
                  <span class="ecb-label">Name</span>
                  <span class="ecb-val">{{ clickedRequest.nameH || "-" }}</span>
                </div>

                <div class="ecb-row">
                  <span class="ecb-label">Home Phone</span>
                  <span class="ecb-val">{{
                    clickedRequest.homePhone || "-"
                  }}</span>
                </div>
                <div class="ecb-row">
                  <span class="ecb-label">Address</span>
                  <span class="ecb-val">{{
                    clickedRequest.address || "-"
                  }}</span>
                </div>
              </div>
            </v-flex>

            <!-- Right column: employer -->
            <v-flex xs12 sm6 class="ecb-col">
              <div class="ecb-section ecb-section--secondary">
                <div class="ecb-section-title ecb-section-title--secondary">
                  Employer
                </div>
                <div class="ecb-row">
                  <span class="ecb-label">Employer Name</span>
                  <span class="ecb-val">{{
                    clickedRequest.employerName || "-"
                  }}</span>
                </div>
                <div class="ecb-row">
                  <span class="ecb-label">Employer Phone</span>
                  <span class="ecb-val">{{
                    clickedRequest.employerPhone || "-"
                  }}</span>
                </div>
                <div class="ecb-row">
                  <span class="ecb-label">Employer Email</span>
                  <span class="ecb-val">{{
                    clickedRequest.employerEmail || "-"
                  }}</span>
                </div>
                <div class="ecb-row">
                  <span class="ecb-label">Contact Person</span>
                  <span class="ecb-val">{{
                    clickedRequest.contactPerson || "-"
                  }}</span>
                </div>
                <div class="ecb-row">
                  <span class="ecb-label">Contact Person Title</span>
                  <span class="ecb-val">{{
                    clickedRequest.contactPersonTitle || "-"
                  }}</span>
                </div>
              </div>
            </v-flex>
          </v-layout>
        </div>

        <!-- Footer -->
        <div class="ecb-footer">
          <v-btn color="default" dark @click="closeDetailsDialog">Close</v-btn>
          <v-spacer></v-spacer>
          <v-btn
            v-if="isAdmin()"
            color="error"
            outline
            @click="openDeleteDialog(clickedRequest)"
          >
            <v-icon left small>mdi-delete</v-icon>
            Delete Request
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- ── Unlink business confirm ── -->
    <v-dialog v-model="unlinkBusinessConfirm" max-width="480" persistent>
      <v-card class="pa-3">
        <v-card-title class="headline pl-0 pt-0">
          <v-icon color="error" class="mr-2">mdi-link-off</v-icon>
          Unlink Business
        </v-card-title>
        <div class="mb-3">
          Unlink
          <span class="font-weight-medium">{{
            (linkedBusiness && linkedBusiness.name) || "this business"
          }}</span>
          from this family's request? Their children will come off this
          employer's bill for the term.
        </div>
        <v-card-actions class="pr-0 pt-2 pb-0 mr-0">
          <v-spacer></v-spacer>
          <v-btn color="default" dark @click="unlinkBusinessConfirm = false"
            >Cancel</v-btn
          >
          <v-btn
            color="error"
            :loading="unlinkingBusiness"
            @click="unlinkBusiness"
          >
            <v-icon left small>mdi-link-off</v-icon>
            Unlink
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ── Delete confirmation dialog ── -->
    <v-dialog v-model="deleteDialog" max-width="500" persistent>
      <v-card class="pa-3">
        <v-card-title class="headline pl-0 pt-0">
          <v-icon color="error" class="mr-2">mdi-alert-circle</v-icon>
          Delete Request?
        </v-card-title>
        <div class="body-1">
          You are about to permanently delete request
          <strong>#{{ requestToDelete.id }}</strong>
          <span v-if="requestToDelete.nameH">
            for <strong>{{ requestToDelete.nameH }}</strong></span
          >
          along with
          <strong>{{ requestToDelete.studentCount || 0 }}</strong>
          linked student(s). Any business link for this account and term will
          also be removed.
        </div>
        <v-alert :value="true" type="warning" class="mt-3 mb-0" outline>
          This action cannot be undone. The request, its students, and the
          account's business link for this term will be permanently lost.
        </v-alert>
        <v-card-actions class="pr-0 pt-4 pb-0 mr-0">
          <v-spacer></v-spacer>
          <v-btn color="default" dark @click="deleteDialog = false">
            Cancel
          </v-btn>
          <v-btn :loading="deleting" color="error" @click="confirmDelete">
            <v-icon left small>mdi-delete</v-icon>
            Yes, Delete Permanently
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import moment from "moment";
import AssignAccountLookup from "../components/AssignAccountLookup.vue";
import BusinessLookup from "../components/BusinessLookup.vue";
import { formatCurrency } from "../../utils/helpers.js";

const STATUS_META = {
  new: { icon: "mdi-circle", color: "#ff9800" },
  "in progress": { icon: "mdi-progress-clock", color: "#fdb400" },
  sent: { icon: "mdi-email-check-outline", color: "#2e7d32" },
  completed: { icon: "mdi-check-circle", color: "#2e7d32" },
};

function getStatusMeta(status) {
  const key = (status || "").trim().toLowerCase();

  return (
    STATUS_META[key] || { icon: "mdi-help-circle-outline", color: "#757575" }
  );
}

export default {
  components: { AssignAccountLookup, BusinessLookup },
  data: () => ({
    title: "Employer Childcare Benefit Requests",
    url: "EmployerChildcareBenefitRequests",
    term: "",
    currentTerm: "",
    actions: [],
    detailsDialog: false,
    deleteDialog: false,
    loadingStudents: false,
    saving: false,
    deleting: false,
    clickedRequest: {},
    requestToDelete: {},
    requestSource: null,
    requestedAgentName: null,
    students: [],
    linkedBusiness: null,
    loadingBusiness: false,
    businessToLink: null,
    linkingBusiness: false,
    unlinkingBusiness: false,
    unlinkBusinessConfirm: false,
    linkCoverageStudents: [],
    linkSelectedStudentIds: [],
    linkCoverageStart: "",
    linkCoverageEnd: "",
    linkStartMenu: false,
    linkEndMenu: false,
    addDialog: false,
    creating: false,
    loadingAccountInfo: false,
    addAccountId: "",
    addTerm: "",
    addExistingRequest: null,
    addSelectedBusiness: null,
    addCoverageStart: "",
    addCoverageEnd: "",
    addStudents: [],
    addSelectedStudentIds: [],
    addSubmitAttempted: false,
    addForm: {
      employerName: "",
      contactPerson: "",
      contactPersonTitle: "",
      employerPhone: "",
      employerEmail: "",
      note: "",
    },
    contactPersonTitleOptions: [
      "Owner",
      "President",
      "CEO",
      "CFO",
      "Controller",
      "Office Manager",
      "HR Manager",
      "Payroll Manager",
      "Bookkeeper",
      "Accountant",
      "Manager",
    ],
    statusOptions: ["New", "In Progress", "Sent", "Completed"],
    updateForm: {
      status: "",
      note: "",
      sentToEmployer: false,
    },
    columns: [
      {
        field: "statusIcon",
        isIcon: true,
        template: "icon",
        alignment: "center",
        visible: true,
        allowFiltering: false,
        allowHeaderFiltering: false,
        allowHiding: false,
        allowSearch: false,
        cssClass: "width-25",
        headerFilter: {
          allowSearch: false,
          groupInterval: undefined,
          height: undefined,
          searchMode: "contains",
          width: undefined,
        },
        icon: (value, row) =>
          getStatusMeta((row && row.status) || value).icon,
        iconColor: (value, row) =>
          getStatusMeta((row && row.status) || value).color,
      },
      {
        label: "Account ID",
        field: "accountID",
        link: "Account View",
        template: "link-cell-template",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Name",
        field: "nameH",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Home Phone",
        field: "homePhone",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Address",
        field: "address",
        visible: false,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Term",
        field: "term",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Employer Name",
        field: "employerName",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Linked Business",
        field: "businessName",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Source",
        field: "source",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Employer Phone",
        field: "employerPhone",
        visible: false,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Employer Email",
        field: "employerEmail",
        visible: false,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Contact Person",
        field: "contactPerson",
        visible: false,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Contact Person Title",
        field: "contactPersonTitle",
        visible: false,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Status",
        field: "status",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Students",
        field: "studentCount",
        type: "number",
        visible: true,
        width: 100,
        alignment: "center",
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Sent By",
        field: "sentBy",
        visible: false,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Sent Date",
        field: "sentDate",
        type: "date",
        format: "MM/dd/yyyy hh:mm:ss a",
        visible: false,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Note",
        field: "note",
        visible: false,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Date Saved",
        field: "dateSaved",
        type: "date",
        format: "MM/dd/yyyy hh:mm:ss a",
        sortOrder: "desc",
        sortIndex: 0,
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
    ],
  }),
  watch: {},
  computed: {
    selectableAddStudents() {
      return this.addStudents.filter((s) => this.isEmployerBenefitEligible(s));
    },
    allAddStudentsSelected() {
      return (
        this.selectableAddStudents.length > 0 &&
        this.selectableAddStudents.every((s) =>
          this.addSelectedStudentIds.includes(
            this.normalizeStudentId(s.studentID)
          )
        )
      );
    },
    someAddStudentsSelected() {
      return (
        this.selectableAddStudents.some((s) =>
          this.addSelectedStudentIds.includes(
            this.normalizeStudentId(s.studentID)
          )
        ) && !this.allAddStudentsSelected
      );
    },
    addSelectedTotal() {
      return this.selectableAddStudents
        .filter((s) =>
          this.addSelectedStudentIds.includes(
            this.normalizeStudentId(s.studentID)
          )
        )
        .reduce((sum, s) => sum + this.getChildCare(s), 0);
    },
    addEmployerNameInvalid() {
      return this.addSubmitAttempted && !(this.addForm.employerName || "").trim();
    },
    addContactPersonInvalid() {
      return this.addSubmitAttempted && !(this.addForm.contactPerson || "").trim();
    },
    addEmployerPhoneInvalid() {
      const phone = (this.addForm.employerPhone || "").trim();
      return this.addSubmitAttempted && (!phone || !this.isPhoneValid(phone));
    },
    addEmployerEmailInvalid() {
      const email = (this.addForm.employerEmail || "").trim();
      return this.addSubmitAttempted && (!email || !this.isEmailValid(email));
    },
    addStudentsInvalid() {
      return this.addSubmitAttempted && this.addSelectedStudentIds.length === 0;
    },
    canSubmitAdd() {
      return (
        !!this.addAccountId &&
        !this.loadingAccountInfo &&
        !this.addExistingRequest &&
        this.selectableAddStudents.length > 0
      );
    },
  },
  methods: {
    async beforeSetupGrid() {
      const [terms, currentRegID] = await Promise.all([
        this.loadTerms(),
        this.loadCurrentRegID(),
      ]);

      this.term = currentRegID || "";
      this.currentTerm = this.term;

      this.actions.push({
        text: "Term",
        type: "select",
        align: "left",
        clearable: true,
        placeholder: "All terms",
        values: terms,
        value: this.term,
        onSelected: this.onTermSelected,
      });

      this.actions.push({
        text: "Add Request",
        action: this.openAddDialog,
      });

      this.buildUrl();
    },
    async loadCurrentRegID() {
      try {
        const response = await this.$http.get(
          "api/employerChildcareBenefitRequests/GetCurrentRegID"
        );

        return response && response.value ? response.value : response;
      } catch (e) {
        return "";
      }
    },
    buildUrl() {
      this.url = this.term
        ? "EmployerChildcareBenefitRequests?term=" +
          encodeURIComponent(this.term) +
          "&status="
        : "EmployerChildcareBenefitRequests?term=&status=";
    },
    onTermSelected(value) {
      this.term = value || "";

      this.buildUrl();

      if (this.$refs.grid) {
        this.$refs.grid.reloadDataSource(this.url);
      }
    },
    async loadTerms() {
      try {
        const response = await this.$http.get(
          "api/employerChildcareBenefitRequests/GetTerms"
        );

        return this.unwrap(response);
      } catch (e) {
        return [];
      }
    },
    unwrap(response) {
      const data = response && response.value ? response.value : response;

      return Array.isArray(data) ? data : [];
    },
    normalizeStudentId(id) {
      if (id == null || String(id).trim() === "") return null;

      return String(id).trim();
    },
    formatCurrency(value) {
      return formatCurrency(value || 0);
    },
    isPhoneValid(phone) {
      const digits = (phone || "").replace(/\D/g, "");

      return /^[0-9+()\-.\s]+$/.test(phone) && digits.length >= 10 && digits.length <= 15;
    },
    isEmailValid(email) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    },
    getChildCare(student) {
      if (!student) return 0;
      if (student.childCareCharge != null) return Number(student.childCareCharge) || 0;

      return student.childCare != null ? Number(student.childCare) || 0 : 0;
    },
    getVoucherStatus(student) {
      switch (((student && student.statusCategory) || "").trim().toLowerCase()) {
        case "approved":
          return "Approved";
        case "pending":
          return "Pending";
      }

      return this.getChildCareCategory(student) !== "Not Eligible"
        ? "Not Enrolled"
        : "N/A";
    },
    getChildCareCategory(student) {
      if (student && student.childCareCategory) return student.childCareCategory;
      if (this.getChildCare(student) > 0) return "After School";

      return "Not Eligible";
    },
    isBilledToVouchers(student) {
      return this.getVoucherStatus(student) === "Approved" && this.getChildCare(student) > 0;
    },
    isVoucherPending(student) {
      return this.getVoucherStatus(student) === "Pending" && this.getChildCare(student) > 0;
    },
    isEmployerBenefitEligible(student) {
      if (this.getChildCareCategory(student) === "Not Eligible") return false;
      if (this.isVoucherPending(student)) return false;

      return !!(student && student.eligibleForEmployerBenefit);
    },
    ineligibleReason(student) {
      if (this.isBilledToVouchers(student)) return "Covered by voucher";
      if (this.isVoucherPending(student)) return "Pending voucher approval";
      if (this.getChildCareCategory(student) === "Not Eligible") return "Not eligible";
      if (this.getChildCare(student) <= 0) return "No Child Care charge";

      return "Not eligible";
    },
    emptyAddForm() {
      return {
        employerName: "",
        contactPerson: "",
        contactPersonTitle: "",
        employerPhone: "",
        employerEmail: "",
        note: "",
      };
    },
    openAddDialog() {
      this.addDialog = true;
      this.creating = false;
      this.loadingAccountInfo = false;
      this.addAccountId = "";
      this.addTerm = this.currentTerm || this.term || "";
      this.addExistingRequest = null;
      this.addSelectedBusiness = null;
      this.addCoverageStart = "";
      this.addCoverageEnd = "";
      this.addStudents = [];
      this.addSelectedStudentIds = [];
      this.addSubmitAttempted = false;
      this.addForm = this.emptyAddForm();

      this.$nextTick(() => {
        if (this.$refs.addAccountLookup) {
          this.$refs.addAccountLookup.clear();
        }
        if (this.$refs.addBusinessLookup) {
          this.$refs.addBusinessLookup.clear();
        }
      });
    },
    closeAddDialog() {
      this.addDialog = false;
      this.addAccountId = "";
      this.addExistingRequest = null;
      this.addSelectedBusiness = null;
      this.addCoverageStart = "";
      this.addCoverageEnd = "";
      this.addStudents = [];
      this.addSelectedStudentIds = [];
      this.addSubmitAttempted = false;
      this.addForm = this.emptyAddForm();

      if (this.$refs.addAccountLookup) {
        this.$refs.addAccountLookup.clear();
      }
      if (this.$refs.addBusinessLookup) {
        this.$refs.addBusinessLookup.clear();
      }
    },
    onAddBusinessSelected(business) {
      this.addSelectedBusiness = business || null;

      if (!business) {
        return;
      }

      // Only fill in fields the admin hasn't already typed something into.
      if (!(this.addForm.employerName || "").trim() && business.name) {
        this.addForm.employerName = business.name;
      }
      if (!(this.addForm.contactPerson || "").trim() && business.contactPerson) {
        this.addForm.contactPerson = business.contactPerson;
      }
      if (
        !(this.addForm.contactPersonTitle || "").trim() &&
        business.contactPersonTitle
      ) {
        this.addForm.contactPersonTitle = business.contactPersonTitle;
      }
      if (!(this.addForm.employerPhone || "").trim() && business.phone) {
        this.addForm.employerPhone = business.phone;
      }
      if (!(this.addForm.employerEmail || "").trim() && business.email) {
        this.addForm.employerEmail = business.email;
      }
    },
    onAddAccountSelected(account) {
      if (!account || !account.accountID) {
        this.addAccountId = "";
        this.addExistingRequest = null;
        this.addStudents = [];
        this.addSelectedStudentIds = [];

        return;
      }

      this.addAccountId = account.accountID;
      this.loadAccountInfo(account.accountID);
    },
    async loadAccountInfo(accountId) {
      this.loadingAccountInfo = true;
      this.addExistingRequest = null;
      this.addStudents = [];
      this.addSelectedStudentIds = [];
      this.addSubmitAttempted = false;

      try {
        const response = await this.$http.get(
          "api/employerChildcareBenefitRequests/GetAccountInfo?accountId=" +
            encodeURIComponent(accountId)
        );

        this.addTerm = (response && response.term) || this.currentTerm || this.term || "";
        this.addExistingRequest = (response && response.existingRequest) || null;
        this.addStudents = this.unwrap(response && response.students);
        this.loadAddCoverageRange();
      } catch (e) {
        this.addStudents = [];
        this.$notify({
          text: this.getErrorMessage(e, "Failed to load account information"),
          group: "notification",
          type: "error",
        });
      } finally {
        this.loadingAccountInfo = false;
      }
    },
    async loadAddCoverageRange() {
      try {
        const response = await this.$http.get(
          "api/businesses/getYearRange?term=" + encodeURIComponent(this.addTerm || "")
        );
        this.addCoverageStart = this.toDateInput(response.yearStart);
        this.addCoverageEnd = this.toDateInput(response.yearEnd);
      } catch (e) {
        this.addCoverageStart = "";
        this.addCoverageEnd = "";
      }
    },
    toggleAllAddStudents() {
      if (this.allAddStudentsSelected) {
        this.addSelectedStudentIds = [];
      } else {
        this.addSelectedStudentIds = this.selectableAddStudents
          .map((s) => this.normalizeStudentId(s.studentID))
          .filter((id) => id);
      }
    },
    openExistingFromAdd() {
      const existing = this.addExistingRequest;

      this.closeAddDialog();

      if (existing) {
        this.onRowClicked(existing);
      }
    },
    async createRequest() {
      this.addSubmitAttempted = true;

      if (!this.addAccountId) {
        this.$notify({
          text: "Please select an account",
          group: "notification",
          type: "error",
        });
        return;
      }

      const studentIds = (this.addSelectedStudentIds || []).filter((id) => id);

      if (!studentIds.length) {
        this.$notify({
          text: "Please select at least one child care student",
          group: "notification",
          type: "error",
        });
        return;
      }

      const employerName = (this.addForm.employerName || "").trim();
      if (!employerName) {
        this.$notify({
          text: "Please enter the business name",
          group: "notification",
          type: "error",
        });
        return;
      }

      const contactPerson = (this.addForm.contactPerson || "").trim();
      if (!contactPerson) {
        this.$notify({
          text: "Please enter a contact person",
          group: "notification",
          type: "error",
        });
        return;
      }

      const phone = (this.addForm.employerPhone || "").trim();
      if (!phone || !this.isPhoneValid(phone)) {
        this.$notify({
          text: "Please enter a valid employer phone number",
          group: "notification",
          type: "error",
        });
        return;
      }

      const email = (this.addForm.employerEmail || "").trim();
      if (!email || !this.isEmailValid(email)) {
        this.$notify({
          text: "Please enter a valid employer email address",
          group: "notification",
          type: "error",
        });
        return;
      }

      try {
        this.creating = true;

        await this.$http.post("api/employerChildcareBenefitRequests/Create", {
          accountID: this.addAccountId,
          employerName,
          contactPerson,
          contactPersonTitle: (this.addForm.contactPersonTitle || "").trim(),
          employerPhone: phone,
          employerEmail: email,
          note: (this.addForm.note || "").trim(),
          studentIds,
          businessID: this.addSelectedBusiness
            ? this.addSelectedBusiness.businessID
            : null,
          coverageStartDate: this.addCoverageStart || null,
          coverageEndDate: this.addCoverageEnd || null,
        });

        this.$notify({
          text: this.addSelectedBusiness
            ? "Request created and linked to " + this.addSelectedBusiness.name
            : "Request created successfully",
          group: "notification",
          type: "success",
        });

        this.closeAddDialog();

        if (this.$refs.grid) {
          this.$refs.grid.reloadGrid();
        }
      } catch (e) {
        this.$notify({
          text: this.getErrorMessage(e, "Failed to create request"),
          group: "notification",
          type: "error",
        });
      } finally {
        this.creating = false;
      }
    },
    isAdmin() {
      const isAdmin = this.$store.getters["authentication/isAdmin"];

      if (!isAdmin || isAdmin == "false") return false;

      return true;
    },
    getStatusIcon(status) {
      return getStatusMeta(status).icon;
    },
    getStatusColor(status) {
      return getStatusMeta(status).color;
    },
    formatDateTime(value) {
      if (!value) return "-";

      return moment(value).format("MM/DD/YYYY hh:mm:ss a");
    },
    formatDate(value) {
      if (!value) return "";
      const m = moment(value);
      return m.isValid() ? m.format("MM/DD/YYYY") : "";
    },
    toDateInput(value) {
      if (!value) return "";
      const m = moment(value);
      return m.isValid() ? m.format("YYYY-MM-DD") : "";
    },
    async onRowClicked(row) {
      const id = row.id != null ? row.id : row.iD;
      this.clickedRequest = Object.assign({}, row, { id: id, iD: id });

      this.updateForm.status = row.status || "";
      this.updateForm.note = row.note || "";
      this.updateForm.sentToEmployer = !!row.sentToEmployer;

      this.detailsDialog = true;
      this.businessToLink = null;
      this.linkedBusiness = null;
      this.loadingBusiness = true;
      this.requestSource = row.source || null;
      this.requestedAgentName = row.requestedAgentName || null;

      if (this.$refs.detailsBusinessLookup) {
        this.$refs.detailsBusinessLookup.clear();
      }

      await Promise.all([
        this.loadStudents(),
        this.loadBusiness(),
        this.loadYearRange(),
        this.loadSource(),
      ]);
    },
    async loadSource() {
      const id = this.clickedRequest.id;

      if (!id && id !== 0) {
        return;
      }

      try {
        const response = await this.$http.get(
          "api/employerChildcareBenefitRequests/GetSource?requestId=" + id
        );

        this.requestSource = (response && response.source) || null;
        this.requestedAgentName = (response && response.requestedAgentName) || null;
      } catch (e) {
        // Keep whatever we already had from the grid row, if anything.
      }
    },
    async loadYearRange() {
      try {
        const term = this.clickedRequest.term || this.term;
        const response = await this.$http.get(
          "api/businesses/getYearRange?term=" + encodeURIComponent(term || "")
        );
        this.linkCoverageStart = this.toDateInput(response.yearStart);
        this.linkCoverageEnd = this.toDateInput(response.yearEnd);
      } catch (e) {
        this.linkCoverageStart = "";
        this.linkCoverageEnd = "";
      }
    },
    prepareLinkStudents() {
      this.linkCoverageStudents = this.students || [];
      this.linkSelectedStudentIds = (this.students || [])
        .map((s) => this.normalizeStudentId(s.studentID))
        .filter((id) => id);
    },
    async loadBusiness() {
      const id = this.clickedRequest.id;

      this.loadingBusiness = true;

      if (!id && id !== 0) {
        this.linkedBusiness = null;
        this.loadingBusiness = false;

        return;
      }

      try {
        const response = await this.$http.get(
          "api/employerChildcareBenefitRequests/GetBusiness?requestId=" + id
        );

        this.linkedBusiness = response || null;
      } catch (e) {
        this.linkedBusiness = null;
      } finally {
        this.loadingBusiness = false;
      }
    },
    onBusinessToLinkSelected(business) {
      this.businessToLink = business || null;

      if (business && business.businessID) {
        this.prepareLinkStudents();
      }
    },
    async linkBusiness() {
      const requestId = this.clickedRequest.id;

      if ((!requestId && requestId !== 0) || !this.businessToLink) return;

      try {
        this.linkingBusiness = true;

        await this.$http.post(
          "api/employerChildcareBenefitRequests/LinkBusiness",
          {
            requestID: requestId,
            businessID: this.businessToLink.businessID,
            students: (this.linkSelectedStudentIds || []).map((id) => ({
              studentID: id,
              coverageStartDate: this.linkCoverageStart,
              coverageEndDate: this.linkCoverageEnd,
            })),
          }
        );

        this.$notify({
          text: "Linked to business successfully",
          group: "notification",
          type: "success",
        });

        await this.loadBusiness();
        await this.loadStudents();
      } catch (e) {
        this.$notify({
          text: this.getErrorMessage(e, "Failed to link business"),
          group: "notification",
          type: "error",
        });
      } finally {
        this.linkingBusiness = false;
      }
    },
    openUnlinkBusinessDialog() {
      this.unlinkBusinessConfirm = true;
    },
    async unlinkBusiness() {
      const requestId = this.clickedRequest.id;

      if (!requestId && requestId !== 0) return;

      try {
        this.unlinkingBusiness = true;

        await this.$http.post(
          "api/employerChildcareBenefitRequests/UnlinkBusiness?requestId=" +
            requestId
        );

        this.linkedBusiness = null;
        this.unlinkBusinessConfirm = false;

        if (this.$refs.detailsBusinessLookup) {
          this.$refs.detailsBusinessLookup.clear();
        }

        this.$notify({
          text: "Business unlinked",
          group: "notification",
          type: "success",
        });
      } catch (e) {
        this.$notify({
          text: this.getErrorMessage(e, "Failed to unlink business"),
          group: "notification",
          type: "error",
        });
      } finally {
        this.unlinkingBusiness = false;
      }
    },
    async loadStudents() {
      const id = this.clickedRequest.id;

      if (!id && id !== 0) {
        this.students = [];

        return;
      }

      try {
        this.loadingStudents = true;

        const response = await this.$http.get(
          "api/employerChildcareBenefitRequests/GetStudents?requestId=" + id
        );

        this.students = this.unwrap(response);
        this.prepareLinkStudents();
      } catch (e) {
        this.students = [];
        this.$notify({
          text: "Failed to load students",
          group: "notification",
          type: "error",
        });
      } finally {
        this.loadingStudents = false;
      }
    },
    async saveRequest() {
      const id = this.clickedRequest.id;

      if (!id && id !== 0) return;

      // "Sent to Employer" is no longer editable from this dialog — pass the
      // existing value straight through so it isn't clobbered.
      const body = {
        iD: id,
        status: this.updateForm.status,
        note: this.updateForm.note,
        sentToEmployer: !!this.clickedRequest.sentToEmployer,
        sentBy: this.clickedRequest.sentBy,
        sentDate: this.clickedRequest.sentDate,
      };

      try {
        this.saving = true;

        await this.$http.post(
          "api/employerChildcareBenefitRequests/Save",
          body
        );

        this.clickedRequest.status = body.status;
        this.clickedRequest.note = body.note;
        this.clickedRequest.sentToEmployer = body.sentToEmployer;
        this.clickedRequest.sentBy = body.sentBy;
        this.clickedRequest.sentDate = body.sentDate;

        this.$notify({
          text: "Request updated successfully",
          group: "notification",
          type: "success",
        });

        if (this.$refs.grid) {
          this.$refs.grid.reloadGrid();
        }
      } catch (e) {
        this.$notify({
          text: this.getErrorMessage(e, "Failed to update request"),
          group: "notification",
          type: "error",
        });
      } finally {
        this.saving = false;
      }
    },
    openDeleteDialog(request) {
      if (!this.isAdmin()) return;

      this.requestToDelete = Object.assign({}, request);
      this.deleteDialog = true;
    },
    async confirmDelete() {
      const id = this.requestToDelete.id;

      if ((!id && id !== 0) || !this.isAdmin()) return;

      try {
        this.deleting = true;

        await this.$http.post(
          "api/employerChildcareBenefitRequests/Delete?id=" + id
        );

        this.$notify({
          text: "Request deleted successfully",
          group: "notification",
          type: "success",
        });

        this.deleteDialog = false;
        this.detailsDialog = false;

        if (this.$refs.grid) {
          this.$refs.grid.reloadGrid();
        }
      } catch (e) {
        this.$notify({
          text: this.getErrorMessage(e, "Failed to delete request"),
          group: "notification",
          type: "error",
        });
      } finally {
        this.deleting = false;
      }
    },
    getErrorMessage(error, fallback) {
      if (error && error.Message) return error.Message;
      if (error && error.message) return error.message;
      if (
        error &&
        error.response &&
        error.response.data &&
        error.response.data.Message
      )
        return error.response.data.Message;

      return fallback;
    },
    closeDetailsDialog() {
      this.detailsDialog = false;
      this.clickedRequest = {};
      this.requestSource = null;
      this.requestedAgentName = null;
      this.students = [];
      this.updateForm.status = "";
      this.updateForm.note = "";
      this.updateForm.sentToEmployer = false;
      this.linkedBusiness = null;
      this.loadingBusiness = false;
      this.businessToLink = null;
      this.unlinkBusinessConfirm = false;

      if (this.$refs.detailsBusinessLookup) {
        this.$refs.detailsBusinessLookup.clear();
      }
    },
  },
};
</script>

<style>
/* ── Dialog shell ── */
.ecb-dialog {
  background: #fff;
  border-radius: 10px;
  display: flex !important;
  flex-direction: column;
  max-height: 92vh;
  overflow: hidden;
}

/* ── Header ── */
.ecb-header {
  display: flex;
  flex-shrink: 0;
  align-items: flex-start;
  justify-content: space-between;
  background: #4caf50;
  color: #fff;
  padding: 20px 24px 16px;
}
.ecb-header-id {
  font-size: 18px;
  font-weight: 700;
  margin-right: 8px;
  opacity: 0.85;
}
.ecb-header-name {
  font-size: 18px;
  font-weight: 600;
  display: flex;
  align-items: baseline;
  gap: 2px;
}
.ecb-header-ref {
  font-size: 12px;
  opacity: 0.8;
  margin-top: 6px;
}
.ecb-header-right {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
}
.ecb-status-chip.v-chip {
  font-weight: 600;
  letter-spacing: 0.3px;
}
.ecb-sent-meta {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.8);
  text-align: right;
}
.ecb-source-chip.v-chip {
  height: 20px;
  font-size: 10.5px;
  font-weight: 600;
  vertical-align: middle;
  margin: 0 0 0 2px;
}
.ecb-source-chip.v-chip .v-chip__content {
  padding: 0 8px;
}

/* ── Body ── */
.ecb-body {
  padding: 20px 24px 0;
  flex: 1;
  overflow-y: auto;
  min-height: 0;
}
.ecb-sections {
  margin-bottom: 4px;
}
.ecb-col {
  padding-bottom: 4px;
}

/* ── Section ── */
.ecb-section {
  margin-bottom: 18px;
}
.ecb-panel {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 10px 12px 6px;
  margin-bottom: 12px;
  background: #fafafa;
}
.ecb-panel--fill {
  display: flex;
  flex-direction: column;
  min-height: 260px;
}
.ecb-panel-head {
  display: flex;
  align-items: center;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: #2e7d32;
  border-bottom: 2px solid #e8f5e9;
  padding-bottom: 5px;
  margin-bottom: 10px;
}
.ecb-panel-divider {
  font-size: 10.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: #b0bec5;
  margin: 10px 0 2px;
}
.ecb-add-grid {
  display: flex;
  align-items: flex-start;
  gap: 14px;
}
.ecb-add-col {
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
}
.ecb-add-hint {
  font-size: 12.5px;
  color: #607d8b;
  line-height: 1.4;
}
.ecb-add-placeholder {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #90a4ae;
  font-size: 13px;
  padding: 24px 0;
}
.ecb-employer-fields {
  margin: 0 -12px -10px;
}
.ecb-employer-fields > .flex {
  padding: 0 12px 10px;
}
.ecb-employer-fields .v-text-field,
.ecb-employer-fields .v-input {
  font-size: 13.5px;
}
.ecb-employer-fields .v-input {
  margin-top: 6px;
  padding-top: 10px;
}
.ecb-section-title {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: #2e7d32;
  border-bottom: 2px solid #e8f5e9;
  padding-bottom: 4px;
  margin-bottom: 8px;
}
.ecb-section-help {
  font-size: 13px;
  font-weight: 500;
  line-height: 1.45;
  color: #37474f;
}

/* ── Label / value row ── */
.ecb-row {
  display: flex;
  align-items: flex-start;
  padding: 5px 0;
  border-bottom: 1px solid #f5f5f5;
  font-size: 13.5px;
  line-height: 1.4;
}
.ecb-row:last-child {
  border-bottom: none;
}
.ecb-label {
  flex: 0 0 168px;
  color: #757575;
  padding-right: 10px;
}
.ecb-val {
  flex: 1;
  color: #212121;
  font-weight: 500;
  word-break: break-word;
}

/* ── Business link panel — promoted, primary ── */
.ecb-link-section {
  margin-bottom: 16px;
  padding: 16px 18px;
  border: 2px solid #90caf9;
  border-radius: 10px;
  background: #f4f9ff;
}
.ecb-link-section--linked {
  border-color: #a5d6a7;
  background: #f3fbf4;
}
.ecb-link-section--loading {
  border-color: #cfd8dc;
  background: #fafafa;
}
.ecb-link-section--loading .ecb-link-section-title {
  color: #607d8b;
}
.ecb-business-loading {
  display: flex;
  align-items: center;
  min-height: 56px;
  padding: 8px 4px;
  color: #607d8b;
  font-size: 13.5px;
}
.ecb-link-section-header {
  display: flex;
  align-items: flex-start;
  margin-bottom: 12px;
}
.ecb-link-section-title {
  font-size: 15px;
  font-weight: 700;
  color: #1b3a57;
  line-height: 1.3;
}
.ecb-link-section--linked .ecb-link-section-title {
  color: #1b5e20;
}
.ecb-link-section-sub {
  font-size: 12.5px;
  color: #607d8b;
  margin-top: 2px;
}
.ecb-business-linked-card {
  display: flex;
  align-items: center;
  padding: 10px 14px;
  border: 1px solid #dcedc8;
  border-radius: 8px;
  background: #fff;
}
.ecb-business-linked-info {
  flex: 1;
  min-width: 0;
}
.ecb-business-linked-name {
  font-size: 15.5px;
  font-weight: 700;
  color: #1b5e20;
}
.ecb-business-linked-meta {
  font-size: 12.5px;
  color: #757575;
  margin-top: 1px;
}
.ecb-unlink-btn {
  color: #b0bec5 !important;
  font-size: 11.5px;
  margin-left: 4px;
}
.ecb-business-picker {
  padding-top: 2px;
}
.ecb-link-students {
  border: 1px solid #e8f5e9;
  border-radius: 8px;
  padding: 10px 12px;
  background: #fff;
}

/* ── Compact secondary update panel ── */
.ecb-update-panel-compact {
  margin: 0 0 18px;
  padding: 8px 12px;
  border: 1px solid #eceff1;
  border-radius: 8px;
  background: #fafafa;
}

/* ── Secondary Family/Employer info ── */
.ecb-sections--secondary {
  opacity: 0.92;
}
.ecb-section--secondary {
  margin-bottom: 4px;
}
.ecb-section-title--secondary {
  color: #90a4ae;
  border-bottom-color: #f5f5f5;
  font-size: 10.5px;
}

/* ── Students table ── */
.ecb-members-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
  margin-top: 6px;
}
.ecb-members-title {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: #2e7d32;
}
.ecb-members-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #4caf50;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  border-radius: 10px;
  min-width: 24px;
  height: 20px;
  padding: 0 7px;
}
.ecb-members-wrap {
  border: 1px solid #dcedc8;
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 8px;
}
.ecb-members-table {
  width: 100%;
  border-collapse: collapse;
  background: #fff;
  font-size: 13.5px;
}
.ecb-members-table thead th {
  background: #f1f8e9;
  color: #33691e;
  font-weight: 700;
  font-size: 11.5px;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  padding: 10px 16px;
  text-align: left;
  border-bottom: 2px solid #dcedc8;
  white-space: nowrap;
}
.ecb-members-table tbody td {
  padding: 10px 16px;
  color: #212121;
  border-bottom: 1px solid #f1f8e9;
  white-space: nowrap;
}
.ecb-members-table tbody tr:last-child td {
  border-bottom: none;
}
.ecb-member-row:hover td {
  background: #f9fbe7;
}
.ecb-td-name {
  font-weight: 600;
}
.ecb-td-empty {
  text-align: center;
  padding: 20px 16px !important;
  color: #9e9e9e;
  font-style: italic;
}

/* ── Add request students ── */
.ecb-add-loading {
  display: flex;
  align-items: center;
  color: #616161;
  font-size: 13.5px;
  padding: 8px 0 16px;
}
.ecb-add-student-list {
  border: 1px solid #dcedc8;
  border-radius: 8px;
  padding: 6px 12px 8px;
  background: #fff;
  margin-bottom: 6px;
  max-height: 46vh;
  overflow-y: auto;
}
.ecb-add-student-list--error {
  border-color: #e57373;
}
.ecb-add-student-item {
  margin-top: 0;
}
.ecb-add-student-item .v-label {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 8px;
}
.ecb-add-student-item--disabled {
  opacity: 0.72;
}
.ecb-add-student-name {
  font-weight: 600;
  color: #212121;
  font-size: 13.5px;
}
.ecb-add-student-meta {
  color: #757575;
  font-size: 12.5px;
}
.ecb-add-student-price {
  color: #2e7d32;
  font-weight: 600;
  font-size: 13px;
  margin-left: auto;
}
.ecb-add-price-note {
  font-weight: 400;
  color: #9e9e9e;
  font-size: 11.5px;
  margin-left: 4px;
}
.ecb-add-student-ineligible {
  color: #ef6c00;
  font-size: 12px;
  margin-left: auto;
}
.ecb-add-total {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 10px;
  padding: 8px 12px;
  background: #f1f8e9;
  border-radius: 6px;
  font-weight: 600;
  color: #1b5e20;
}
.ecb-add-error-text {
  color: #c62828;
  font-size: 12.5px;
  margin-top: 4px;
}

/* ── Footer ── */
.ecb-footer {
  display: flex;
  flex-shrink: 0;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  padding: 14px 24px 16px;
  border-top: 1px solid #f0f0f0;
  background: #fff;
}
</style>
